// COPOMOptionsCard.js
import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

// MT5 pit stream (live, real-time) — kept as the preferred price source.
const API_BASE = process.env.REACT_APP_API_URL || '/api/go';
// Auto-discovery service: cached COPOM option ladder from cotacao.b3.com.br.
const COPOM_API = '/api/copom';

// Capitalize a "set/2026" style meeting label -> "Set/2026" for display.
const prettyMeeting = (m) => (m ? m.charAt(0).toUpperCase() + m.slice(1) : '--');

export const COPOMOptionsCard = ({ theme }) => {
  const [optionsData, setOptionsData] = useState([]);
  const [marketConsensus, setMarketConsensus] = useState(null);
  const [baseSelic, setBaseSelic] = useState(15);
  const [nextMeeting, setNextMeeting] = useState('--');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLiveOptions();
  }, []);

  const fetchLiveOptions = async () => {
    try {
      setLoading(true);

      // 1. DISCOVERY + BASE DATA: cached cotacao ladder (auto-discovered tickers).
      const res = await fetch(COPOM_API);
      if (!res.ok) throw new Error(`COPOM service ${res.status}`);
      const copom = await res.json();

      const base = typeof copom.base_selic === 'number' ? copom.base_selic : 15;
      setBaseSelic(base);
      setNextMeeting(prettyMeeting(copom.meeting));

      const strikes = Array.isArray(copom.strikes) ? copom.strikes : [];

      // 2. HYBRID LIVE PRICES: overlay Nick's MT5 pit stream when available;
      //    fall back to the cotacao (delayed) values from /api/copom otherwise.
      const merged = await Promise.all(
        strikes.map(async (s) => {
          let price = s.price;      // cotacao delayed (may be null)
          let volume = s.volume;    // cotacao delayed (may be null)
          let dailyChange = 0;
          try {
            const mt5 = await fetch(`${API_BASE}/api/ticker/${s.ticker}`);
            if (mt5.ok) {
              const d = await mt5.json();
              const livePrice = (d.fec != null ? d.fec : d.ult);
              if (livePrice != null) price = livePrice;   // prefer live pit price
              if (d.vol != null) volume = d.vol;          // prefer live volume
              if (d.var != null) dailyChange = d.var;
            }
          } catch (err) {
            // /api/go unavailable — degrade gracefully to cotacao values.
          }
          return {
            ticker: s.ticker,
            label: s.label,
            changeVal: s.changeVal,
            impliedSelic: (typeof s.impliedSelic === 'number') ? s.impliedSelic : base + s.changeVal,
            price,
            volume: volume || 0,
            dailyChange,
          };
        })
      );

      // Sort by implied Selic (low -> high) for the chart.
      const sortedData = merged.sort((a, b) => a.impliedSelic - b.impliedSelic);
      setOptionsData(sortedData);

      // 3. CONSENSUS: from the service response; enrich with merged (live) fields.
      let consensus = null;
      if (copom.consensus && copom.consensus.ticker) {
        consensus = sortedData.find((r) => r.ticker === copom.consensus.ticker) || null;
      }
      if (!consensus && sortedData.length > 0) {
        // Fallback: highest volume locally.
        consensus = sortedData.reduce((prev, cur) => (cur.volume > prev.volume ? cur : prev));
      }
      setMarketConsensus(consensus);
    } catch (err) {
      console.error('Critical Error fetching COPOM options:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        background: theme.cardBg,
        borderRadius: '16px',
        padding: '1.5rem',
        border: `1px solid ${theme.border}`,
        minHeight: '280px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.textSecondary
      }}>
        Carregando dados da B3...
      </div>
    );
  }

  return (
    <div style={{
      background: theme.cardBg,
      borderRadius: '16px',
      padding: '1.5rem',
      border: `1px solid ${theme.border}`,
      backdropFilter: 'blur(10px)',
      transition: 'all 0.3s'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1.5rem' }}>
        <div>
          <div style={{
            fontSize: '0.75rem', color: theme.textSecondary, textTransform: 'uppercase',
            fontWeight: 'bold', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem'
          }}>
            🏛️ Opções COPOM (B3)
            <span style={{ background: 'rgba(212, 175, 55, 0.2)', color: '#D4AF37', padding: '2px 8px', borderRadius: '4px', fontSize: '0.65rem' }}>
              LIVE
            </span>
          </div>
          <h3 style={{ fontSize: '1.2rem', fontWeight: 'bold', color: theme.textPrimary, margin: 0 }}>
            Taxa Selic Implícita
          </h3>
        </div>

        <div style={{
          background: 'rgba(212, 175, 55, 0.1)', padding: '0.5rem 1rem',
          borderRadius: '8px', border: '1px solid rgba(212, 175, 55, 0.3)'
        }}>
          <div style={{ fontSize: '0.7rem', color: theme.textSecondary }}>Próxima Reunião</div>
          <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#D4AF37' }}>{nextMeeting}</div>
        </div>
      </div>

      {/* Main Stats Display */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div>
          <div style={{
            fontSize: '3rem', fontWeight: 'bold', color: '#D4AF37',
            fontFamily: 'monospace', lineHeight: 1
          }}>
            {marketConsensus ? `${marketConsensus.impliedSelic.toFixed(2)}%` : '--'}
          </div>
          <div style={{ fontSize: '0.85rem', color: theme.textSecondary, marginTop: '0.5rem' }}>
            Consenso de Mercado: <strong style={{ color: theme.textPrimary }}>
               {marketConsensus?.label || '--'}
            </strong>
          </div>
        </div>

        {/* Dynamic Sentiment Indicator */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{
            width: '60px', height: '60px', borderRadius: '50%',
            background: marketConsensus?.changeVal < 0
                ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(21, 128, 61, 0.3))' // Green/Dovish
                : 'linear-gradient(135deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.3))', // Red/Hawkish
            border: `2px solid ${marketConsensus?.changeVal < 0 ? 'rgba(34, 197, 94, 0.5)' : 'rgba(239, 68, 68, 0.5)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem'
          }}>
            {marketConsensus?.changeVal < 0 ? '📉' : '📈'}
          </div>
          <span style={{
              fontSize: '0.7rem', fontWeight: 'bold',
              color: marketConsensus?.changeVal < 0 ? '#22c55e' : '#ef4444'
          }}>
            {marketConsensus?.changeVal < 0 ? 'DOVISH' : 'HAWKISH'}
          </span>
        </div>
      </div>

      {/* Volume Distribution Chart */}
      <div style={{ marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.75rem', color: theme.textSecondary, marginBottom: '0.75rem', textTransform: 'uppercase', fontWeight: 'bold' }}>
          Volume por Probabilidade (Contratos)
        </div>

        <ResponsiveContainer width="100%" height={120}>
          <BarChart data={optionsData}>
            <XAxis
              dataKey="label"
              stroke={theme.textSecondary}
              fontSize={10}
              tick={{fill: theme.textSecondary}}
            />
            <Tooltip
              contentStyle={{ background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: '8px' }}
              cursor={{fill: 'rgba(255,255,255,0.05)'}}
              formatter={(value, name) => [value.toLocaleString(), 'Volume']}
            />
            <Bar dataKey="volume" radius={[4, 4, 0, 0]}>
              {optionsData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.ticker === marketConsensus?.ticker ? '#D4AF37' : 'rgba(212, 175, 55, 0.3)'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Footer Info */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem',
        paddingTop: '1rem', borderTop: `1px solid ${theme.border}`
      }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: theme.textSecondary }}>Selic Base</div>
          <div style={{ fontSize: '1rem', fontWeight: 'bold', color: theme.textPrimary }}>{baseSelic.toFixed(2)}%</div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: theme.textSecondary }}>Contrato Líder</div>
          <div style={{ fontSize: '1rem', fontWeight: 'bold', color: theme.textPrimary }}>
             {(marketConsensus?.volume || 0).toLocaleString()} <span style={{fontSize: '0.6rem'}}>vol</span>
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.7rem', color: theme.textSecondary }}>Variação (Dia)</div>
          <div style={{
              fontSize: '1rem', fontWeight: 'bold',
              color: (marketConsensus?.dailyChange || 0) >= 0 ? '#22c55e' : '#ef4444'
          }}>
            {(marketConsensus?.dailyChange || 0).toFixed(2)}%
          </div>
        </div>
      </div>
    </div>
  );
};

export default COPOMOptionsCard;
